import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import ProfileHeader from '../components/ProfileHeader';
import ProfileTabs from '../components/ProfileTabs';
import VideoGrid from '../components/VideoGrid';
import EditProfileModal from '../components/EditProfileModal';
import UploadButton from '../../video/components/UploadButton';
import toast from 'react-hot-toast';

interface Video {
  id: string;
  video_url: string;
  thumbnail_url: string | null;
  title: string;
  likes_count: number;
  comments_count: number;
  is_paid: boolean;
  is_promoted: boolean;
  price: number;
}

const ProfilePage: React.FC = () => {
  const { userId } = useParams();
  const currentUser = getCurrentUser();
  const [activeTab, setActiveTab] = useState<'videos' | 'liked' | 'saved' | 'purchased'>('videos');
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const isOwnProfile = userId === currentUser?.id;

  useEffect(() => {
    if (userId) {
      loadVideos();
    }
  }, [userId, activeTab]);

  const loadVideos = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      let query;

      switch (activeTab) {
        case 'videos':
          // Get user's own videos
          const { data: ownVideos, error: ownError } = await supabase
            .from('ponavideos')
            .select('*')
            .eq('user_id', userId)
            .eq('is_visible', true)
            .order('created_at', { ascending: false });

          if (ownError) throw ownError;
          setVideos(ownVideos || []);
          break;

        case 'liked':
          // Get videos the user has liked
          const { data: likedVideos, error: likedError } = await supabase
            .from('ponovideo_likes')
            .select(`
              video_id,
              video:ponavideos(*)
            `)
            .eq('user_id', userId)
            .eq('ponavideos.is_visible', true)
            .order('created_at', { ascending: false });

          if (likedError) throw likedError;
          setVideos(likedVideos?.map(v => v.video) || []);
          break;

        case 'saved':
          // Get videos the user has saved
          const { data: savedVideos, error: savedError } = await supabase
            .from('video_saves')
            .select(`
              video_id,
              video:ponavideos(*)
            `)
            .eq('user_id', userId)
            .eq('ponavideos.is_visible', true)
            .order('created_at', { ascending: false });

          if (savedError) throw savedError;
          setVideos(savedVideos?.map(v => v.video) || []);
          break;

        case 'purchased':
          // Get videos the user has purchased
          const { data: purchasedVideos, error: purchasedError } = await supabase
            .from('ponovideoaccess')
            .select(`
              video_id,
              video:ponavideos(*)
            `)
            .eq('user_id', userId)
            .eq('ponavideos.is_visible', true)
            .order('created_at', { ascending: false });

          if (purchasedError) throw purchasedError;
          setVideos(purchasedVideos?.map(v => v.video) || []);
          break;
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = () => {
    loadVideos();
  };

  return (
    <div className="min-h-screen bg-black">
      <ProfileHeader
        onEditProfile={() => setShowEditModal(true)}
        onSettings={() => {/* Handle settings */}}
      />

      <ProfileTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <VideoGrid
          videos={videos}
          loading={loading}
        />
      </div>

      {isOwnProfile && (
        <UploadButton onUploadComplete={loadVideos} />
      )}

      {showEditModal && (
        <EditProfileModal
          onClose={() => setShowEditModal(false)}
          onUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default ProfilePage;
